import { useState } from 'react';
import { Link } from 'react-router';
import {
  CASE_HISTORY_FILTERS,
  STAGE_LABEL,
  caseTimeline,
  proofsOfEntry,
  type CaseHistoryFilter,
  type CaseHistoryItem,
  type CasePhoto,
  type Stage,
  type StageHistoryEntry,
} from '@/domain';
import { useCaseActivity } from '@/application/usecases/useKits';
import { proofItem } from '@/features/photos/photoViewerItems';
import { openPhotoTile } from '@/features/photos/photoViewerStore';
import { formatDuration } from '@/application/timer/timerStore';
import { formatDay, formatInstant } from '@/shared/format/instant';
import { strings } from '@/shared/strings';
import { SkeletonRows } from '@/ui/feedback/Loading';
import { CameraIcon } from '@/ui/icons';
import styles from './CaseHistory.module.css';

const FILTER_LABEL: Record<CaseHistoryFilter, string> = {
  all: strings.record.filterAll,
  bench: strings.record.filterBench,
  moves: strings.record.filterMoves,
  notes: strings.record.filterNotes,
  accounts: strings.record.filterAccounts,
  edits: strings.record.filterEdits,
};

export interface CaseHistoryProps {
  jobOrderNo: string;
  /** The bench entries of the case's trays, with their durations and notes. */
  stages: readonly StageHistoryEntry[];
  /** Show only this many of the newest rows, with a way to the rest. */
  limit?: number;
  /**
   * Where the whole history is, for a caller who may open the case record. A
   * caller who may not, a technician on the job view, opens it in place instead.
   */
  wholeHistoryTo?: string;
  /**
   * The proofs of finished work on the case. A stage entry that has one shows a
   * camera marker that opens the image, against the right stage, on the right
   * day, by the right person. Section 2.4 of the request of 13 September 2026.
   */
  proofs?: readonly CasePhoto[];
  /**
   * Where the case's trays are now. A move to another bench is a line, not a
   * bench entry, until somebody starts work there, so without this the newest
   * bench in the history read as where the case still was.
   */
  currentStages?: readonly Stage[];
}

/**
 * Case history: everything that happened to one case, from the job order being
 * created to the customer receiving it, in one timeline.
 *
 * The bench entries keep their dot, their duration and their note; every other
 * action is a lighter line with who did it, what they did and when, all in lab
 * time. Grouped by lab day, newest first, with a filter for the three things a
 * manager opening a long case is usually after.
 *
 * The names are the server's business. A doctor's or patient's name reaches this
 * card composed onto a row only for a caller who may read it, and is absent for
 * everybody else, so nothing here has to hide anything.
 */
export function CaseHistory({
  jobOrderNo,
  stages,
  limit,
  wholeHistoryTo,
  proofs = [],
  currentStages = [],
}: CaseHistoryProps) {
  const [filter, setFilter] = useState<CaseHistoryFilter>('all');
  const [expanded, setExpanded] = useState(false);
  const { data, isPending, isError } = useCaseActivity(jobOrderNo);

  const all = caseTimeline(stages, data?.items ?? [], filter);
  const cut = limit !== undefined && !expanded && all.length > limit;
  const items = cut ? all.slice(0, limit) : all;

  // Grouped by the lab's day, in the order the rows already stand.
  const days: { day: string; items: CaseHistoryItem[] }[] = [];
  for (const item of items) {
    const day = formatDay(item.at);
    const last = days[days.length - 1];
    if (last?.day === day) last.items.push(item);
    else days.push({ day, items: [item] });
  }

  return (
    <section className={styles.card} aria-labelledby={`history-${jobOrderNo}`}>
      <h4 id={`history-${jobOrderNo}`}>{strings.record.caseHistory}</h4>
      {currentStages.length > 0 && (
        <p className={styles.now}>
          {strings.record.nowAt}{' '}
          <b>{[...new Set(currentStages)].map((stage) => STAGE_LABEL[stage]).join(', ')}</b>
        </p>
      )}

      <div className={styles.filters} role="group" aria-label={strings.record.filterLabel}>
        {CASE_HISTORY_FILTERS.map((key) => (
          <button
            key={key}
            type="button"
            className={`${styles.filter} ${filter === key ? styles.on : ''}`}
            aria-pressed={filter === key}
            onClick={() => setFilter(key)}
          >
            {FILTER_LABEL[key]}
          </button>
        ))}
      </div>

      {isError && <p className={styles.none}>{strings.errors.server}</p>}
      {isPending && stages.length === 0 && (
        <SkeletonRows rows={3} label={strings.record.loadingHistory} />
      )}

      {!isError && !(isPending && stages.length === 0) && items.length === 0 && (
        <p className={styles.none}>
          {filter === 'all' ? strings.record.noHistoryYet : strings.record.noHistoryForFilter}
        </p>
      )}

      {days.map(({ day, items: rows }) => (
        <div key={day} className={styles.day}>
          <h5 className={styles.dayHeading}>{day}</h5>
          <ol className={styles.timeline}>
            {rows.map((item) =>
              item.kind === 'stage' ? (
                <StageRow
                  key={item.entry.id}
                  entry={item.entry}
                  proofs={proofsOfEntry(proofs, item.entry.id)}
                  jobOrderNo={jobOrderNo}
                />
              ) : (
                <li key={item.entry.id} className={`${styles.entry} ${styles.action}`}>
                  <span className={styles.dot} />
                  <div className={styles.line}>
                    <b>{item.entry.byUserName ?? strings.activity.system}</b> {item.entry.action}
                    {item.entry.target !== jobOrderNo && item.entry.target !== '' && (
                      <> {item.entry.target}</>
                    )}
                  </div>
                  <div className={styles.meta}>
                    <time className={styles.time} dateTime={item.entry.createdAt}>
                      {formatInstant(item.entry.createdAt)}
                    </time>
                  </div>
                </li>
              ),
            )}
          </ol>
        </div>
      ))}

      {cut &&
        (wholeHistoryTo === undefined ? (
          <button type="button" className={styles.more} onClick={() => setExpanded(true)}>
            {strings.record.showWholeHistory}
          </button>
        ) : (
          <Link className={styles.more} to={wholeHistoryTo}>
            {strings.record.showWholeHistory}
          </Link>
        ))}
    </section>
  );
}

function StageRow({
  entry,
  proofs,
  jobOrderNo,
}: {
  entry: StageHistoryEntry;
  proofs: CasePhoto[];
  jobOrderNo: string;
}) {
  const tone =
    entry.note !== null ? styles.flagged : entry.endedAt === null ? styles.open : styles.done;

  return (
    <li className={`${styles.entry} ${tone}`}>
      <span className={styles.dot} />
      <div className={styles.name}>
        {STAGE_LABEL[entry.stage]}
        {entry.note !== null && <span className={styles.note}>{entry.note}</span>}
        {proofs.length > 0 && <ProofMarker proofs={proofs} jobOrderNo={jobOrderNo} />}
      </div>
      <div className={styles.meta}>
        {entry.byUserName ?? strings.record.unassigned} ·{' '}
        <time className={styles.time} dateTime={entry.startedAt}>
          {formatInstant(entry.startedAt)}
        </time>
        {entry.endedAt === null ? (
          <> · {strings.record.stillHere}</>
        ) : (
          entry.activeSeconds > 0 && (
            <>
              {' · '}
              <span className={styles.time}>{formatDuration(entry.activeSeconds)}</span>{' '}
              {strings.record.activeTime}
            </>
          )
        )}
      </div>
    </li>
  );
}

/**
 * A small camera on a stage entry that has a proof of work; tapping it opens
 * the photographs of that stage visit in the image viewer, every one of them.
 * The request of 5 October 2026 (third), section 6.
 *
 * It was a link to the first of them in a new tab, whatever number it showed,
 * and not a link at all until that one's bytes had arrived. It fetches nothing
 * now: the viewer does.
 */
function ProofMarker({ proofs, jobOrderNo }: { proofs: CasePhoto[]; jobOrderNo: string }) {
  const count = proofs.length;
  const label = count > 1 ? `${strings.proof.marker} (${String(count)})` : strings.proof.marker;

  return (
    <button
      type="button"
      className={styles.proof}
      aria-label={label}
      title={label}
      onClick={(event) =>
        openPhotoTile(
          event,
          proofs.map((proof) => proofItem(proof)),
          0,
          jobOrderNo,
        )
      }
    >
      <CameraIcon />
      {count > 1 && <span>{count}</span>}
    </button>
  );
}
